import { GRACE_WINDOW_MS } from '../../common/hooks/useGraceWindow';
import type { Direction } from '../Dashboard/components/DashboardTables/LegalCases/legalCasesType';

export enum RISK_HEADER_KEYS {
    AML_CHECK_KEY = 'AmlCheck',
    PEP_CHECK_KEY = 'PepCheck',
    SANCTION_CHECK_KEY = 'SanctionCheck',
    STATE_OWNED_ENTERPRISES_KEY = 'State Owned Enterprises',
    RISK_INDICATOR_KEY = 'riskProfile',
    APC_CHECK_KEY = 'ApcCheck',
    LEGAL_CASES_KEY = 'legalCasesAgainst',
    LEGAL_CASES_BY_KEY = 'legalCasesBy',
    WATCHLISTS_KEY = 'Watchlists',
}

export enum RISK_HEADER_VALUES {
    AML_CHECK = 'AML Penalties',
    PEP_CHECK = 'Politically Exposed Person',
    SANCTION_CHECK = 'Global Sanctions',
    STATE_OWNED_ENTERPRISES = 'State Owned Enterprises',
    APC_CHECK = 'Adverse Press Coverage',
    LEGAL_CASES = 'Legal Cases Against',
    LEGAL_CASES_BY = 'Legal Cases Filed By',
    WATCHLISTS = 'Watchlists',
}

export enum HEADER_MESSAGES {
    RISK_INDICATOR_MESSAGE = 'It aggregates key compliance checks, including high-risk jurisdictions, PEPs, sanctions, SOEs, adverse press coverage, and global watchlists. It streamlines risk assessment to identify and mitigate threats like fraud, corruption, and money laundering.',
    AML_CHECK_MESSAGE = 'The AML check refers to the AML Penalties check,  covers all money laundering events and the associated entities convicted over the past 24 years. This check ensures you are not working with someone involved in past money laundering activities.',
    PEP_CHECK_MESSAGE = 'PEP stands for Politically Exposed Persons, referring to individuals who hold or have held prominent public positions, along with their immediate family members and close associates. Because of their influence and access to significant financial resources, PEPs are considered higher-risk for money laundering, bribery, and corruption.',
    SANCTION_CHECK_MESSAGE = 'Global sanctions, which are restrictive measures imposed by governments or international organizations (e.g., the United Nations or European Union) on countries, entities, or individuals suspected of being involved in illicit activities like money laundering, terrorist financing, or human rights violations. These sanctions are designed to restrict financial transactions, trade, and activities supporting illegal operations.',
    STATE_OWNED_ENTERPRISES_MESSAGE = 'State-Owned Enterprises are businesses or corporations in which the government holds a majority or complete ownership stake. Due to their size, global reach, and involvement in large financial transactions, SOEs can be vulnerable to abuse by criminal actors for money laundering or terrorist financing and hence are considered to be high risk to work with.',
    APC_CHECK_MESSAGE = 'Adverse Press Coverage screens for negative media reports on individuals or entities, highlighting risks like fraud, corruption, money laundering, or regulatory violations. This check helps identify potential reputational and compliance risks early, ensuring informed decision-making.',
    // Condenses the three tooltips this column merges: the mandatory lists enforced by Indian
    // regulators, global sanctions/regulatory watchlists, and further lists from other countries.
    WATCHLISTS_MESSAGE = 'Screening against curated watchlists maintained by regulators, investigative bodies and quasi-judicial authorities. Covers the mandatory Indian lists enforced by bodies such as the CBI, RBI, FIU and SEBI; global regulatory and sanctions watchlists from over 150 countries; and further lists that fall outside global sanctions but are screened as best practice. Entries flag potential political exposure, financial crime, national security risk or compliance violations.',
    // Deliberately omits a Confirmed-vs-Probable
    // explanation, which would be false for the ~94% of prod rows where confidenceLevel is absent.
    LEGAL_CASES_MESSAGE = 'Court cases in which the company or its directors are named as a respondent, identified by matching legal name or address against public court records. Matches are indicative — please verify before relying on them.',
    // Copy decision (not in the ticket's written spec): mirrors LEGAL_CASES_MESSAGE with the
    // entity's role flipped to petitioner, since this column reads the same court records for
    // cases the entity filed rather than cases filed against it.
    LEGAL_CASES_BY_MESSAGE = 'Court cases in which the company or its directors are named as a petitioner, identified by matching legal name or address against public court records. Matches are indicative — please verify before relying on them.',
}

export const RISK_HEADERS = [
    { key: RISK_HEADER_KEYS.LEGAL_CASES_KEY, label: RISK_HEADER_VALUES.LEGAL_CASES },
    { key: RISK_HEADER_KEYS.LEGAL_CASES_BY_KEY, label: RISK_HEADER_VALUES.LEGAL_CASES_BY },
    { key: RISK_HEADER_KEYS.PEP_CHECK_KEY, label: RISK_HEADER_VALUES.PEP_CHECK },
    { key: RISK_HEADER_KEYS.SANCTION_CHECK_KEY, label: RISK_HEADER_VALUES.SANCTION_CHECK },
    { key: RISK_HEADER_KEYS.AML_CHECK_KEY, label: RISK_HEADER_VALUES.AML_CHECK },
    { key: RISK_HEADER_KEYS.APC_CHECK_KEY, label: RISK_HEADER_VALUES.APC_CHECK },
    { key: RISK_HEADER_KEYS.WATCHLISTS_KEY, label: RISK_HEADER_VALUES.WATCHLISTS },
    {
        key: RISK_HEADER_KEYS.STATE_OWNED_ENTERPRISES_KEY,
        label: RISK_HEADER_VALUES.STATE_OWNED_ENTERPRISES,
    },
];

export const RISK_MESSAGES_MAP: Record<RISK_HEADER_KEYS, string> = {
    [RISK_HEADER_KEYS.RISK_INDICATOR_KEY]: HEADER_MESSAGES.RISK_INDICATOR_MESSAGE,
    [RISK_HEADER_KEYS.AML_CHECK_KEY]: HEADER_MESSAGES.AML_CHECK_MESSAGE,
    [RISK_HEADER_KEYS.APC_CHECK_KEY]: HEADER_MESSAGES.APC_CHECK_MESSAGE,
    [RISK_HEADER_KEYS.PEP_CHECK_KEY]: HEADER_MESSAGES.PEP_CHECK_MESSAGE,
    [RISK_HEADER_KEYS.SANCTION_CHECK_KEY]: HEADER_MESSAGES.SANCTION_CHECK_MESSAGE,
    [RISK_HEADER_KEYS.STATE_OWNED_ENTERPRISES_KEY]: HEADER_MESSAGES.STATE_OWNED_ENTERPRISES_MESSAGE,
    [RISK_HEADER_KEYS.LEGAL_CASES_KEY]: HEADER_MESSAGES.LEGAL_CASES_MESSAGE,
    [RISK_HEADER_KEYS.LEGAL_CASES_BY_KEY]: HEADER_MESSAGES.LEGAL_CASES_BY_MESSAGE,
    [RISK_HEADER_KEYS.WATCHLISTS_KEY]: HEADER_MESSAGES.WATCHLISTS_MESSAGE,
};

export const RISK_DOT_COLOR_MAP = {
    GREEN: 'var(--risk-indicator-green)',
    YELLOW: 'var(--risk-indicator-yellow)',
    RED: 'var(--risk-indicator-red)',
    NOT_AVAILABLE: 'var(--risk-indicator-notAvailable)',
} as const;

export const RISK_TYPES = [
    { color: RISK_DOT_COLOR_MAP.RED, label: 'Confirmed Risk' },
    { color: RISK_DOT_COLOR_MAP.YELLOW, label: 'Potential Risk' },
    { color: RISK_DOT_COLOR_MAP.GREEN, label: 'No Risk' },
    { color: RISK_DOT_COLOR_MAP.NOT_AVAILABLE, label: 'Not Available' },
];

export enum TYPES {
    ORGANIZATION = 'organization',
    INDIVIDUAL = 'individual',
}

export const UW_BEFE = 'UW_FETCH_AML_PEP_DATA';
export const UW_REGULATORY_RISK_HITS_BEFE = 'UW_FETCH_REGULATORY_RISK_HITS';
export const DOWNLOAD_ERROR_MESSAGE = 'Failed to download report. Please try again later.';
export const AICA_GET_TASK_TRACKER_DATA = 'AICA_GET_TASK_TRACKER_DATA';
export const AION_INGEST_AML_PEP_DATA = 'AION_INGEST_AML_PEP_DATA';
export enum RISK_INDICATOR_STATUS {
    PROCESSING = 'PROCESSING',
    COMPLETE = 'COMPLETE',
    FAILED = 'FAILED',
    FAILURE = 'FAILURE',
    TODO = 'TODO',
    TODO_ASYNC = 'TODO_ASYNC',
}
export const MAX_POLLING_ATTEMPTS = 15;

// Lives next to useGraceWindow — LegalCases' poller consumes it too. Re-exported here so
// existing imports keep working.
export { GRACE_WINDOW_MS };
// Defensive ceiling on the post-COMPLETE grace poll — the GRACE_WINDOW_MS timeout is the real
// terminator; this only guards against an interval that somehow outlives the window. Derived
// from the window over the 10s poll interval used by useRiskIndicatorStates' useBoundedPoll
// call, plus one tick of slack, so it always comfortably outlasts the window. Evaluates to 7.
export const GRACE_POLL_ATTEMPTS = Math.ceil(GRACE_WINDOW_MS / 10000) + 1;

/*
    RECUR-27829 cell/section colours, taken from the design (Figma node 34293-27364).
    Deliberately NOT shared with RISK_LABEL_CONFIG below: the legacy score-path pills keep
    their own older hexes (#D32F2F / #FFEBEE, #FFF2CB), and the new regulatory-risk cells use
    the softer design pair. They are near-neighbours, not the same tokens — folding them
    together is what made the first cut render the wrong red.
*/
export const RISK_COLORS = {
    CONFIRMED: { color: '#DF666A', backgroundColor: '#FFEEEE' },
    PROBABLE: { color: '#E4AB01', backgroundColor: '#FEF9EC' },
} as const;

/* Legal Cases cell copy (both columns). */
export const LEGAL_CASES_CLICK_TOOLTIP = 'Click to view legal cases';

export const LEGAL_CASES_NO_MATCH_TOOLTIP: Record<Direction, string> = {
    filed_against: 'Not able to find any legal cases filed against this entity',
    filed_by: 'Not able to find any legal cases filed by this entity',
};

// Per Figma, not the written spec: a leading "+", the count, the word, then "(Disposed)" in
// parentheses — e.g. "+ 1 Confirmed (Disposed)", "+ 118 Probable (Disposed)".
export const LEGAL_CASES_CONFIRMED_DISPOSED_SUB_LABEL = (count: number) => `+ ${count} Confirmed (Disposed)`;
export const LEGAL_CASES_PROBABLE_DISPOSED_SUB_LABEL = (count: number) => `+ ${count} Probable (Disposed)`;

/* Table cell rule colour — the design strokes every cell right + bottom in this grey. */
export const RISK_CELL_BORDER_COLOR = '#E7E7E7';

/*
    RECUR-27829: the per-cell state underwriting-engine computes from the actual hit rows.
    The cell used to be derived from a score; it now reads this. Kept as string literals
    matching the BE enum exactly — they arrive over the wire.
*/
export enum RegulatoryRiskState {
    CONFIRMED = 'CONFIRMED',
    PROBABLE = 'PROBABLE',
    NO_RISK = 'NO_RISK',
    NOT_AVAILABLE = 'NOT_AVAILABLE',
}

/*
    Cell presentation per the RECUR-27829 design: the count leads and the state word follows,
    so a cell reads "2 Confirmed" / "2 Probable". States with no count render `label` alone
    and are not openable, because there is nothing for the drawer to show.

    Every hex here is read off the design (Figma node 34293-27364), not borrowed from the
    legacy pill palette. "No Match" is plain muted text with no fill, where the old
    "No Risk" carried a green pill; "No Data" sits on the design's faint grey wash.
*/
export const RISK_STATE_CONFIG = {
    [RegulatoryRiskState.CONFIRMED]: {
        word: 'Confirmed',
        label: null,
        ...RISK_COLORS.CONFIRMED,
        openable: true,
        tick: false,
    },
    [RegulatoryRiskState.PROBABLE]: {
        word: 'Probable',
        label: null,
        ...RISK_COLORS.PROBABLE,
        openable: true,
        tick: false,
    },
    [RegulatoryRiskState.NO_RISK]: {
        word: null,
        label: 'No Match',
        color: '#9D9D9D',
        backgroundColor: 'transparent',
        openable: false,
        // A per-state LITERAL, not a derived predicate — every one of
        // !openable / word === null / !pill is ALSO true of NOT_AVAILABLE below, so
        // deriving this would tick "No Data" and claim a screening check that never ran.
        tick: true,
    },
    [RegulatoryRiskState.NOT_AVAILABLE]: {
        word: null,
        label: 'No Data',
        color: '#9D9D9D',
        backgroundColor: '#F9FAFC',
        openable: false,
        tick: false,
    },
} as const;

/*
    Drawer section headings. Counts come from the RENDERED rows, never from the BE's
    confirmedCount/probableCount — the drawer applies its own "has any displayable field"
    filter, and pairing a BE count with a filtered list is the "pill said 3, drawer said 2"
    defect recorded in RECUR-24938.

    The section pill is a SOLID fill in the state colour with white text — not the tinted
    background + coloured text the table cells use. The design's pill ("Trade Status",
    borderRadius 16px) fills with #DF666A / #E4AB01 and sets the label to #FFFFFF, uppercase.
    Reusing the cell's tint pair here is what made it read as the wrong colour.
*/
/** "1 Match" / "N Matches" -- the count pills in the regulatory risk drawer. */
export const matchCountLabel = (count: number) => `${count} ${count === 1 ? 'Match' : 'Matches'}`;

export const REGULATORY_RISK_SECTIONS = {
    CONFIRMED: { title: 'Confirmed Matches', pillBackground: RISK_COLORS.CONFIRMED.color, pillColor: '#FFFFFF' },
    PROBABLE: { title: 'Other Probable Matches', pillBackground: RISK_COLORS.PROBABLE.color, pillColor: '#FFFFFF' },
} as const;

export const RISK_LABEL_CONFIG = {
    NO_RISK: {
        label: 'No Risk',
        color: '#49A71B',
        backgroundColor: '#E1FFE1',
    },
    POTENTIAL_RISK: {
        label: 'Potential Risk',
        color: '#E4AB01',
        backgroundColor: '#FFF2CB',
    },
    HIGH_RISK: {
        label: 'High Risk',
        color: '#D32F2F',
        backgroundColor: '#FFEBEE',
    },
    NOT_AVAILABLE: {
        label: 'No Data',
        color: '#9D9D9D',
        backgroundColor: '#EBEBEB',
    },
} as const;
